import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, CalendarCheck, Dumbbell, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const podiumStyles = [
  "border-[#ccff00] shadow-[0_0_30px_rgba(204,255,0,0.25)]",
  "border-white/40",
  "border-[#b87333]/60",
];

export default function MemberWall() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get("/leaderboard").then((r) => setEntries(r.data)).catch(() => {});
  }, []);

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-10 py-20 min-h-[70vh]" data-testid="member-wall-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-4">Member Wall</p>
        <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
          The club's <span className="text-[#ccff00]">grinders.</span>
        </h1>
        <p className="text-white/60 max-w-xl mt-6 leading-relaxed">
          Points for showing up: <span className="text-[#ccff00]">+10</span> per court booking, <span className="text-[#ccff00]">+25</span> per clinic visit, <span className="text-[#ccff00]">+50</span> for joining a tier. Book a court and you'll appear here too.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        {podium.map((e, i) => (
          <motion.div
            key={e.rank}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
            className={`bg-[#0f0f10] border rounded-md p-8 text-center ${podiumStyles[i]} ${i === 0 ? "md:-translate-y-3" : ""}`}
            data-testid={`podium-card-${e.rank}`}
          >
            <Trophy className={`mx-auto mb-4 ${i === 0 ? "text-[#ccff00]" : i === 1 ? "text-white/70" : "text-[#b87333]"}`} size={i === 0 ? 36 : 28} />
            <p className="font-display text-lg text-white/40">#{e.rank}</p>
            <div className="w-16 h-16 rounded-full bg-[#1a1a1a] border-2 border-[#ccff00] flex items-center justify-center font-display text-xl text-[#ccff00] mx-auto my-4">
              {e.initials}
            </div>
            <h3 className="font-display uppercase text-xl tracking-tight">{e.name}</h3>
            {e.tier && <p className="text-[#ccff00] text-xs uppercase tracking-widest mt-1">{e.tier} member</p>}
            <p className="font-display text-4xl text-[#ccff00] mt-4">{e.points}<span className="text-sm text-white/40 font-sans"> pts</span></p>
            <div className="flex justify-center gap-5 mt-4 text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5"><CalendarCheck size={13} className="text-[#ccff00]" /> {e.bookings} bookings</span>
              <span className="inline-flex items-center gap-1.5"><Dumbbell size={13} className="text-[#ccff00]" /> {e.clinic_visits} clinics</span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 border border-[#2a2a2a] rounded-md overflow-hidden" data-testid="leaderboard-list">
        {rest.map((e, i) => (
          <motion.div
            key={e.rank}
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 8) * 0.05, duration: 0.4 }}
            className="flex items-center gap-4 sm:gap-6 px-5 sm:px-8 py-4 border-b border-[#2a2a2a]/60 last:border-b-0 bg-[#0f0f10] hover:bg-white/[0.03] transition-colors"
            data-testid={`leaderboard-row-${e.rank}`}
          >
            <span className="font-display text-white/40 w-8">#{e.rank}</span>
            <span className="w-10 h-10 rounded-full bg-[#1a1a1a] border border-[#ccff00]/50 flex items-center justify-center font-display text-sm text-[#ccff00] shrink-0">
              {e.initials}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">
                {e.name}
                {e.live && <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 px-2 py-0.5 rounded-full">New</span>}
              </p>
              <p className="text-xs text-white/40">{e.bookings} bookings · {e.clinic_visits} clinics{e.tier ? ` · ${e.tier}` : ""}</p>
            </div>
            <span className="font-display text-xl text-[#ccff00]">{e.points}</span>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <h3 className="font-display uppercase text-2xl tracking-tight">Want on the wall?</h3>
          <p className="text-white/60 mt-2">Every demo booking earns you +10 points instantly.</p>
        </div>
        <Link
          to="/courts"
          data-testid="wall-book-cta"
          className="bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-3.5 rounded-full hover:bg-[#a3e635] hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center gap-2 whitespace-nowrap"
        >
          Book a Court <ArrowRight size={16} />
        </Link>
      </div>
    </main>
  );
}
